import { DestroyRef, inject, Injectable } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { NavigationEnd, Router } from "@angular/router";
import { EMPTY, combineLatest, filter, map, merge, of } from "rxjs";

import { ACCOUNT_BLOCKED_PATH } from "../../guards/billing-block.guard";
import { PlatformAlertsService } from "./platform-alerts.service";

/**
 * Routes the total block never applies to: authentication (the user must always be
 * able to sign in to see why they are blocked), embedded-chat (the total block does not
 * cover it) and the OAuth consent pages.
 */
const EXEMPT_PATHS = [
    "/login",
    "/embedded-chat",
    "/oauth",
    "/oauth2",
    "/external-calendar/connect",
];

/**
 * Where an unblocked user leaves the blocked page for. Straight to the landing route:
 * "/" would bounce through /login before reaching it, flashing the login screen.
 */
export const PLATFORM_HOME_PATH = "/conversations";

function matchesPath(url: string, path: string): boolean {
    const pathname = url.split(/[?#]/)[0];
    return pathname === path || pathname.startsWith(`${path}/`);
}

/**
 * Where to send the user given the current block state and URL, or null to stay:
 * blocked users go to the blocked page (unless on an exempt route), and the blocked
 * page sends everyone else back to the platform.
 */
export function resolveBillingBlockRedirect(
    blocked: boolean,
    url: string,
): string | null {
    const onBlockedPage = matchesPath(url, `/${ACCOUNT_BLOCKED_PATH}`);
    if (blocked) {
        const exempt = EXEMPT_PATHS.some((path) => matchesPath(url, path));
        return onBlockedPage || exempt ? null : `/${ACCOUNT_BLOCKED_PATH}`;
    }
    return onBlockedPage ? PLATFORM_HOME_PATH : null;
}

/**
 * Live counterpart of BillingBlockGuard for sessions that are already open: the guard
 * only runs on navigation, this reacts as soon as billingState/current changes, in any
 * subtree (core, business-setup, talkdesk). Started once from AppComponent.
 */
@Injectable({ providedIn: "root" })
export class BillingBlockRedirectService {
    private readonly platformAlertsService = inject(PlatformAlertsService);
    private readonly router = inject(Router);
    private readonly destroyRef = inject(DestroyRef);
    private started = false;

    start(): void {
        if (this.started) return;
        this.started = true;

        // start() runs after AppComponent awaits the app config, so the initial
        // NavigationEnd may already be gone. Only trust router.url once a navigation
        // has completed: before that it is still "/" and could send an embedded-chat
        // load to the blocked page.
        const url$ = merge(
            this.router.navigated ? of(this.router.url) : EMPTY,
            this.router.events.pipe(
                filter((event) => event instanceof NavigationEnd),
                map((event) => event.urlAfterRedirects),
            ),
        );

        combineLatest([this.platformAlertsService.totalBlock$, url$])
            .pipe(
                map(([blocked, url]) =>
                    resolveBillingBlockRedirect(blocked, url),
                ),
                filter((target): target is string => target !== null),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((target) => {
                void this.router.navigateByUrl(target);
            });
    }
}
